import React, { useEffect, useRef, useState } from "react";
import { Button, HStack } from "@chakra-ui/react";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import { createUniqueId, getGeometryTypeGeojson, insertByPriority, matchKeyInText } from "../../../utilies/Utility";
import Type from "../../../utilies/Types";
import { loadGeoJSONToCesium } from "../../../utilies/geojson/LoadGeojson";
import { useMapObjects } from "../../../context/MapObjectsContext";
import { toggleTrafficSignTool } from "../../../utilies/trafficSignTools/Add";
import HoTrafficSign from "../../../icons/HoTrafficSign";

const SIGN_BOX_KEY = "trafficsignbox";
const SIGN_POLE_KEY = "trafficsignpole";

function TrafficSignTool() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const editMode = useAppStore((s) => s.editMode);
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const setEditFeatureDialog = useAppStore((s) => s.setEditFeatureDialog);

  const trafficSignMode = useAppStore((s) => s.trafficSignMode);
  const setTrafficSignMode = useAppStore((s) => s.setTrafficSignMode);
  const userData = useAppStore((s) => s.userData);

  const [isShow, setIsShow] = useState(false);

  // Keep last known datasources for proper disable when store turns mode off externally
  const dsRef = useRef({ box: null, pole: null });

  // Keep stable callback reference (some tools depend on same fn ref for remove listeners)
  const onEditRef = useRef(null);
  if (!onEditRef.current) {
    onEditRef.current = function (_boxFeature, _poleFeature, boxEntity, _poleEntity) {
      try {
        setMapObjects((prev) => {
          const next = [...prev];
          for (const k in next) {
            if (matchKeyInText(next[k].file_name, SIGN_BOX_KEY)) next[k].is_edited = true;
            if (matchKeyInText(next[k].file_name, SIGN_POLE_KEY)) next[k].is_edited = true;
          }
          return next;
        });
        setEditFeatureDialog?.({ open: true, entity: boxEntity, autoAdd: ["type", "text"] });
      } catch {}
    };
  }

  const findDataSources = () => {
    let t = { box: null, pole: null };

    for (const key in mapObjects) {
      const obj = mapObjects[key];
      if (matchKeyInText(obj.file_name, SIGN_BOX_KEY)) t.box = obj.dataSource;
      if (matchKeyInText(obj.file_name, SIGN_POLE_KEY)) t.pole = obj.dataSource;
    }

    return t;
  };

  useEffect(() => {
    const t = findDataSources();
    dsRef.current = t;
    setIsShow(Boolean(t.box && t.pole));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapObjects]);

  const createNewFile = (typeOfLayer) => {
    return new Promise((resolve) => {
      const geojsonData = {
        type: "FeatureCollection",
        crs: {
          type: "name",
          properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" },
        },
        features: [],
      };

      const id = createUniqueId();

      setMapObjects((prevMapObjects) => {
        const newMapObject = {
          id,
          name: "Geojson",
          entities: [],
          type: Type.ASSET.GEOJSON,
          file_name: typeOfLayer + ".geojson",
          file_type: ".geojson",
          inner_entities: [],
          visible: true,
          dataSource: null,
          label_visible: false,
          geojson: geojsonData,
          view_type: "line",
          osm_data: null,
          osmDataSource: null,
          style_stroke_width: null,
          style_fill_color: null,
          style_outline_color: null,
          style_type: null,
          style_pattern: "solid",
          is_center_lane: false,
          auto_select: false,
          show_lanelet: false,
          priority: 0,
          _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
          geojson_type: getGeometryTypeGeojson(geojsonData),
        };

        const updatedMapObjects = insertByPriority(prevMapObjects, newMapObject);

        loadGeoJSONToCesium({
          viewer: viewerRef.current,
          geojson: geojsonData,
          options: { ...defaultGeojsonStyle },
          callback: (dataSource, _geoJson, billboards, styleFillColor, styleOutsideColor, styleStrokeWidth, styleType) => {
            setTimeout(() => {
              setMapObjects((prevState) => {
                const updatedState = [...prevState];
                const objectIndex = updatedState.findIndex((obj) => obj.id === id);

                if (objectIndex !== -1) {
                  const updatedObject = {
                    ...updatedState[objectIndex],
                    dataSource,
                    billboards,
                    style_stroke_width: styleStrokeWidth,
                    style_fill_color: styleFillColor,
                    style_outline_color: styleOutsideColor,
                    style_type: styleType,
                    loading: false,
                    options: { ...defaultGeojsonStyle },
                  };

                  updatedState[objectIndex] = updatedObject;
                  resolve(updatedObject);
                } else {
                  resolve(null);
                }

                return updatedState;
              });
            }, 500);
          },
          geojsonType: Type.ASSET.GEOJSON,
          layerName: typeOfLayer + ".geojson",
          id,
        });

        return updatedMapObjects;
      });
    });
  };

  const disableTool = (box, pole) => {
    try {
      toggleTrafficSignTool(viewerRef.current, onEditRef.current, true, box, pole);
    } catch {}
  };

  const handleToggle = async () => {
    const next = !trafficSignMode;
    setTrafficSignMode(next);

    let t = findDataSources();

    if (next) {
      // Turning ON: ensure layers exist
      if (!t.box) {
        const boxObj = await createNewFile(SIGN_BOX_KEY);
        if (boxObj?.dataSource) t.box = boxObj.dataSource;
      }
      if (!t.pole) {
        const poleObj = await createNewFile(SIGN_POLE_KEY);
        if (poleObj?.dataSource) t.pole = poleObj.dataSource;
      }

      dsRef.current = t;

      // Need both
      if (!t.box || !t.pole) {
        setTrafficSignMode(false);
        disableTool(t.box, t.pole);
        return;
      }

      // Enable tool (disable flag = false)
      toggleTrafficSignTool(viewerRef.current, onEditRef.current, false, t.box, t.pole, userData, mapObjectsRef , setMapObjects );
    } else {
      // Turning OFF: disable tool immediately using last known datasources
      const { box, pole } = dsRef.current || {};
      disableTool(box ?? t.box, pole ?? t.pole);
    }
  };

  // If store turns this mode off externally, actually disable the tool
  useEffect(() => {
    if (trafficSignMode) return;
    const { box, pole } = dsRef.current || {};
    disableTool(box, pole);
  }, [trafficSignMode]);

  // If layers become unavailable (button disappears) while tool is on, force-disable and sync store
  useEffect(() => {
    if (isShow) return;

    if (trafficSignMode) {
      const { box, pole } = dsRef.current || {};
      disableTool(box, pole);
      setTrafficSignMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const { box, pole } = dsRef.current || {};
      disableTool(box, pole);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!trafficSignMode) return;

    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setTrafficSignMode(false);
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [trafficSignMode, setTrafficSignMode]);

  if (!isShow) return null;

  return (
    <HStack>
      <Button
        disabled={editMode}
        variant="solid"
        p={0}
        opacity={1}
        bg={trafficSignMode ? "green" : "white"}
        color={trafficSignMode ? "white" : "MenuText"}
        borderWidth={trafficSignMode ? 2 : 1}
        borderColor={trafficSignMode ? "green" : "gray.200"}
        onClick={handleToggle}
        aria-pressed={trafficSignMode}
      >
        <Tooltip content={trafficSignMode ? "Traffic Sign: ON" : "Add Traffic Sign"}>
          <HoTrafficSign />
        </Tooltip>
      </Button>
    </HStack>
  );
}

export default TrafficSignTool;
